import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";

/**
 * The randomization data layer. Spec 21.8.
 *
 * RTK Query, with the five tags the specification names, invalidated on every
 * mutation. `@reduxjs/toolkit` already ships it, so this adds no dependency —
 * only the reducer and middleware registration in `store.ts`.
 *
 * NO OPTIMISTIC UPDATES ANYWHERE IN THIS MODULE (spec 21.8), and that is not a
 * stylistic preference. An optimistic randomization would show the user an arm
 * or a number that the server may refuse — the list can be exhausted, the
 * stratum may not exist, another request may have taken the slot — and a
 * participant's assignment is not something to display before it is durable.
 * Buttons disable while in flight instead.
 *
 * @category Randomization
 */

/**
 * The configuration lifecycle. SF-D12.
 *
 * `committed` and `active` are NOT the same thing, and the difference decides
 * whether anyone can be randomized. Committed means the sequence exists;
 * active means it is in force. Allocation refuses anything else.
 *
 * `draft` and `committed` were the only two states until 12 September 2026.
 */
export type RandomizationStatus =
  | "draft"
  | "committed"
  | "active"
  | "superseded";

/** SF-D26. `continue` is meaningful only from version 2 onward. */
export type StreamPolicy = "fresh" | "continue";

export type FactorSource = "site" | "question_choice" | "question_number";

export type FactorDerivation =
  | "none"
  | "age_from_year_of_birth"
  | "age_from_date_of_birth";
export type BlindingLevel = "open" | "single" | "double";

export interface RandomizationArm {
  id?: string;
  code: string;
  name: string;
  /** Absent for a requester without `randomization.seed_view`. */
  weight?: number;
  position: number;
}

/**
 * One level of one factor, as the server returns it.
 *
 * `valueToken` is positional — `F1V2` is "the second level of the first
 * factor" — and carries no meaning on its own. That is the whole of SF-D10:
 * a stratum key cannot be decoded by anyone who does not also hold the label
 * map, which is why the level list itself is gated.
 */
export interface RandomizationFactorLevel {
  id: string;
  position: number;
  valueToken: string;
  questionChoiceId: string | null;
  lowerBound: number | null;
  upperBound: number | null;
  studySiteId: string | null;
  label: string | null;
  /** True only for a site level appended after commit under SF-D8. */
  addedAfterCommit: boolean;
}

/**
 * One stratification factor.
 *
 * THE BINDING IS THREE IDS, NOT A QUESTION ID (SF-D2). A question label is
 * editable and two forms can carry questions with the same label, so the
 * screen shows phase, form and question — never the question alone.
 */
export interface RandomizationFactor {
  position: number;
  factorCode: string;
  source: FactorSource;
  label: string | null;
  phaseId: string | null;
  formId: string | null;
  questionId: string | null;
  derivation: FactorDerivation | null;
  levelCount: number;
  /**
   * ABSENT without `randomization.stratum_view`, and that is not tidiness.
   * The ordered level list IS the dictionary that decodes every stratum key in
   * the slot list, so returning it ungated would defeat the permission that
   * gates `stratumLabels`. Any screen that renders levels must handle their
   * absence.
   */
  levels?: RandomizationFactorLevel[];
}

export interface RandomizationConfig {
  id: string;
  method: string;
  mrtEnabled: boolean;
  generationMode: string;
  /** Absent without `randomization.seed_view` — absent, not null (spec 11.1). */
  seed?: number | null;
  blockLengths?: number[] | null;
  listLength: number | null;

  /** 1 for the first configuration of a study. */
  versionNumber: number;
  streamPolicy: StreamPolicy | null;

  /**
   * The factors, in position order. Empty for an unstratified configuration.
   *
   * REPLACES `stratifyBySite`, `stratifyByGender`, `stratifyByAge` and
   * `ageBands`. Those four were the whole stratification model: a study could
   * stratify by site, gender and age, and by nothing else. The columns behind
   * them were dropped by migration 20260912100002 — the server does not send
   * them and never will again.
   */
  factors: RandomizationFactor[];

  /** Token-to-label map written at commit: { F1V1: 'Mild', F1V2: 'Moderate' }.
   *  Absent without `randomization.stratum_view`. */
  stratumLabels: Record<string, string> | null;
  expectedEnrolment: number | null;
  blindingLevel: BlindingLevel;
  status: RandomizationStatus;
  sequenceChecksum: string | null;
  engineVersion: string;
  committedAt: string | null;
  arms: RandomizationArm[];
  offeredBlockLengths?: number[];
}

/**
 * One age band. `maxAge` is null on the open-ended top band.
 *
 * `code` is assigned by the SERVER from the order of the array (A1, A2, ...)
 * and is never sent up. A code the form could set would drift from the band it
 * names.
 */
export interface AgeBand {
  code: string;
  minAge: number;
  maxAge: number | null;
}

export interface PreviewSlot {
  stratumKey: string;
  sequenceIndex: number;
  armCode: string;
  blockId: number | null;
  blockLength: number | null;
  isPartialBlock: boolean;
  randomizationNumber: number;
}

export interface SequencePreview {
  slots: PreviewSlot[];
  totalSlots: number;
  perArm: Record<string, number>;
  perStratum: Record<string, number>;
  /** The same keys, resolved for display: gender and age tokens through the
   *  config's label map, site ids through a study_sites join. */
  perStratumLabels?: Record<string, string>;
  hasPartialFinalBlock: boolean;
  paramDigest: string;
}

export interface ParticipantAllocation {
  allocationId: string | null;
  randomizationNumber: number | null;
  /** Absent unless the requester may see the arm (spec 11.2). */
  armCode?: string;
  armId?: string;
  stratumKey: string | null;
  /**
   * `stratumKey` with every level token resolved through the label map of the
   * configuration THIS ALLOCATION WAS MADE UNDER — not the study's latest.
   * Tokens are positional, so version 2's map would mislabel a version 1
   * allocation. Absent without `randomization.stratum_view`.
   */
  stratumLabel?: string | null;
  /** SF-D25. Which version produced this allocation. */
  configId?: string | null;
  configVersion?: number | null;
  state: string | null;
  attemptSeq: number | null;
  allocatedAt: string | null;
  allocatedById: string | null;
  triggerSource: string | null;
  isRandomized: boolean;
  unblindedAt: string | null;
  reversedAt?: string | null;
  reversalReasonCode?: string | null;
  /** Absent without `randomization.reason_view`. */
  reversalReason?: string;
}

/**
 * One factor as the form sends it.
 *
 * NO `factorCode` AND NO `valueToken`. Both are assigned by the server from
 * array position (SF-D10). A token the form could set could be spelled to
 * carry the value it exists to hide, and a code the form could set would drift
 * from the factor it names.
 *
 * WHICH LEVEL FIELDS APPLY DEPENDS ON `source`, so all are optional here and
 * the server refuses the combinations that make no sense.
 */
export interface SaveFactorBody {
  source: FactorSource;
  /** Required unless `source` is `site`. All three, not just the question. */
  phaseId?: string | null;
  formId?: string | null;
  questionId?: string | null;
  /** `question_number` only. */
  derivation?: FactorDerivation | null;
  label?: string | null;
  levels: {
    questionChoiceId?: string | null;
    /** Half-open: lower <= x < upper. First may omit lower, last may omit upper. */
    lowerBound?: number | null;
    upperBound?: number | null;
    studySiteId?: string | null;
    label?: string | null;
  }[];
}

export interface SaveConfigBody {
  studyId: string;
  method: string;
  mrtEnabled?: boolean;
  generationMode?: string;
  seed?: number;
  arms: { code: string; name: string; weight: number; position: number }[];
  blockLengths?: number[];
  listLength?: number;
  /**
   * The whole factor list, every time — this is not a patch.
   *
   * ORDER IS SIGNIFICANT. Position determines the factor code and the `f<n>`
   * segment of every stratum key, so reordering after commit would rename
   * every stratum. That is why factors are locked at commit (SF-D12).
   *
   * Omitted or empty means unstratified. At most 7 (MAX_FACTORS, derived from
   * the 200-stratum ceiling: a factor needs at least two levels, and 2^8 > 200).
   */
  factors?: SaveFactorBody[];
  /**
   * REQUIRED, not optional. It was optional, `toBody` omitted it, and the
   * server defaulted it to 'open' — so a double-blind study was stored as
   * open label. The one field that controls arm disclosure must not be
   * silently defaultable at either end.
   */
  blindingLevel: BlindingLevel;
  expectedEnrolment?: number;
  acknowledgeSparseStrata?: boolean;
}


/* ------------------------------------------------- factor discovery ------ */

export interface FactorChoice {
  questionChoiceId: string;
  label: string;
  position: number;
}

/**
 * A question a factor may bind to.
 *
 * The server has already filtered: `radio`, `dropdown` and `number` only, and
 * only phases of category `screening` or `visit` (SF-D4). Checkbox is excluded
 * because multiple selections cannot resolve to one stratum; free text because
 * an open value set cannot be enumerated, and enumeration is what produces the
 * sequences. Survey phases are excluded — a participant-completed survey is not
 * a clinical observation the site controls.
 */
export interface FactorCandidate {
  phaseId: string;
  phaseName: string;
  /** Orders the picker, and names the visit in the delay statement. */
  phasePosition: number;
  formId: string;
  formName: string;
  questionId: string;
  label: string;
  type: string;
  questionPosition: number;
  /** Choice questions only, in choice order. */
  choices: FactorChoice[];
  /** Number questions: decimal places the question can record. Null when the
   *  question sets no maximum. Feeds the band-precision check (B6). */
  decimalPrecision: number | null;
  minValue: number | null;
  maxValue: number | null;
  /**
   * True when a factor in the draft already binds this question. SHOWN AND
   * DISABLED, not hidden — hiding it makes the picker disagree with the
   * configuration the designer is looking at.
   */
  inUse: boolean;
}

/* ------------------------------------------- participant readiness ------- */

/**
 * Why a participant cannot be randomized. SFT-17.
 *
 * NOT BEING READY IS NORMAL AND MAY LAST WEEKS — a factor bound to a Week 4
 * question holds every participant unready until Week 4. The dialog renders
 * these as statements of fact, not as failures.
 */
export type NotReadyReason =
  | "NO_ACTIVE_CONFIG"
  | "NOT_ELIGIBLE"
  | "ALREADY_RANDOMIZED"
  | "PARTICIPANT_LOCKED"
  | "FACTOR_UNANSWERED"
  | "SITE_UNKNOWN";

export type FactorStatus =
  | "completed"
  | "not_answered"
  /** Answered, but the answer matches no level of this factor. */
  | "not_covered"
  /** Answered, but the number falls outside every band. */
  | "out_of_range"
  | "site_unknown";

/**
 * One factor, for one participant.
 *
 * `valueToken`, `valueLabel` and `derivedValue` are **absent** without
 * `randomization.stratum_view` — absent, not null. The server deletes the keys
 * (spec 11.2), because a null says a value exists and is being withheld, which
 * is a different statement from "this factor resolved to no level". The screen
 * renders the two differently.
 */
export interface ParticipantFactorRow {
  factorCode: string;
  factorPosition: number;
  label: string | null;
  source: FactorSource;
  questionId: string | null;
  phaseId: string | null;
  phaseName: string | null;
  phasePosition: number | null;
  formId: string | null;
  formName: string | null;
  status: FactorStatus;
  valueToken?: string | null;
  valueLabel?: string | null;
  derivedValue?: number | null;
}

export interface ParticipantFactorStatus {
  ready: boolean;
  reasons: NotReadyReason[];
  /**
   * True once the participant has an active allocation. The rows then come
   * from `randomization_allocation_factors` — what they WERE randomized on,
   * not what their record says today.
   */
  allocated: boolean;
  configId: string | null;
  configVersion: number | null;
  factors: ParticipantFactorRow[];
}

/** One site a `site` factor may take a level from. */
export interface SiteCandidate {
  studySiteId: string;
  name: string;
}

/* -------------------------------------------------- the lifecycle -------- */

export interface CommitResult {
  totalSlots: number;
  sequenceChecksum: string;
  versionNumber: number;
  coreDigest: string;
  scopeDigest: string;
  strataCount: number;
}

export interface ActivateResult {
  configId: string;
  versionNumber: number;
  /** The version this one replaced, or null on the first activation. */
  supersededConfigId: string | null;
  /** Available slots retired from the superseded version. */
  retiredSlotCount: number;
}

export interface WithdrawResult {
  configId: string;
  versionNumber: number;
  /** Withdrawal DELETES the sequence. A withdrawn version was never in force,
   *  so its slots never had standing and do not belong in the retired report. */
  discardedSlotCount: number;
}

export interface ConfigDiffEntry {
  key: string;
  kind: "added" | "removed" | "changed";
  old: unknown;
  new: unknown;
  /** True when this change alone would require a new version. */
  material?: boolean;
  field?: string;
}

export interface AmendResult {
  configId: string;
  versionNumber: number;
  supersedesConfigId: string;
  streamPolicy: StreamPolicy;
  coreDigest: string;
  scopeDigest: string;
  strataCount: number;
  slotCount: number;
  /** Render THIS one. Arm codes intact. */
  diff: ConfigDiffEntry[];
  /** The same diff with arm codes replaced by positions, for the audit row. */
  auditDiff: ConfigDiffEntry[];
  summary: string[];
}

export interface AddSiteStratumResult {
  configId: string;
  versionNumber: number;
  factorCode: string;
  valueToken: string;
  /** Stratum keys that did not exist before this call. */
  newStrata: string[];
  slotsGenerated: number;
  /** The scope digest changes; the core digest must NOT (SF-D8). */
  scopeDigest: string;
  coreDigest: string;
}

/* --------------------------------------------------------------- list ----- */

export interface StudyArg {
  studyId: string;
}

export interface FindSlotsArgs extends StudyArg {
  page?: number;
  pageSize?: number;
  stratumKey?: string;
  status?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface RandomizationSlot {
  id: string;
  randomizationNumber: number;
  stratumKey: string;
  stratumLabel?: string;
  /** Absent — not null — without `randomization.arm_view` (spec 11.2). */
  armCode?: string;
  sequenceIndex?: number;
  blockId?: number | null;
  blockLength?: number | null;
  isPartialBlock?: boolean;
  status: string;
  subjectId: string | null;
  consumedAt: string | null;
  retiredAt: string | null;
  /** Never hidden. A list with a silent gap in the numbering reads as
   *  numbers having been skipped (spec 21.2). */
  retiredReasonCode: string | null;
}

export interface SlotPage {
  items: RandomizationSlot[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * Whether to render the arm column at all.
   *
   * Sent by the server rather than inferred from whether the first row
   * happens to carry `armCode`: an empty page carries no rows, and guessing
   * from row zero would flicker the column in and out as filters change.
   * Spec 21.8 also requires the absence to reach a screen reader as an absent
   * column, which needs a definite answer before render.
   */
  armColumnVisible: boolean;
}

export interface RetiredSlotsReport {
  items: {
    stratumKey: string;
    stratumLabel?: string;
    armCode?: string | null;
    generated: number;
    retired: number;
    consumed: number;
    available: number;
  }[];
  strata: {
    stratumKey: string;
    stratumLabel?: string;
    retired: number;
    generated: number;
  }[];
  armColumnVisible: boolean;
}

export interface StratumDriftReport {
  items: {
    allocationId: string;
    participantId: string;
    subjectId: string | null;
    randomizationNumber: number;
    allocatedStratumKey: string;
    allocatedStratumLabel?: string;
    currentStratumKey: string | null;
    driftField: string;
    allocatedAt: string;
  }[];
  total: number;
  /** False while gender drift cannot be decided in SQL — the stored code is
   *  encrypted. Shown in the UI so the report is not read as "no drift". */
  genderDriftEvaluated: boolean;
}

export interface UnblindingEventsReport {
  items: {
    id: string;
    participantId: string;
    subjectId: string | null;
    allocationId: string | null;
    randomizationNumber: number | null;
    actorName: string | null;
    actorEmail: string | null;
    reasonCode: string;
    unblindingReason?: string;
    armCode?: string | null;
    safetyReference: string | null;
    occurredAt: string;
  }[];
  total: number;
  armColumnVisible: boolean;
}

export interface GenderQuestion {
  linked: boolean;
  questionId: string | null;
  questionLabel: string | null;
  questionType?: string;
  formId: string | null;
  formName: string | null;
  mappedTokens: {
    choiceId: string;
    choiceLabel: string;
    code: string;
    token: string;
  }[];
  /** The reason the checkbox stays disabled even though a question exists. */
  unmappedChoices: {
    choiceId: string;
    choiceLabel: string;
    value: string | null;
  }[];
  availableCodes?: { code: string; token: string }[];
  reason: string | null;
}

/**
 * The year-of-birth screener question, for the age stratification checkbox.
 *
 * A year-of-birth question is a NUMBER question, so `mappedTokens` and
 * `unmappedChoices` are always empty and `linked` turns purely on whether a
 * tagged question exists — there is nothing on a number question to attach a
 * mapping to. The age BANDS live on the randomization configuration instead,
 * because they are per study.
 */
export interface YearOfBirthQuestion extends GenderQuestion {
  attribute: string;
  /** Bounds the question builder should put on the question itself. */
  plausibleYearRange: { min: number; max: number };
  derivationExample: {
    yearOfBirth: number;
    derivedAge: number;
    note: string;
  };
}

export interface ExportRecord {
  id: string;
  fileName: string;
  rowCount: number;
  headerBlockLines: number;
  fileSize: number;
  status: string;
  expiresOn: string;
  /** Five minutes, not the platform's thirty (spec 18). Stated so the UI can
   *  say so rather than implying the usual window. */
  linkMinutes: number;
  singleUse: boolean;
  testData: boolean;
}

/* ---------------------------------------------------------------- MRT ----- */

export interface MrtValidationIssue {
  field: string;
  code: string;
  message: string;
}

export interface MrtTreatmentOption {
  id?: string;
  code: string;
  label?: string | null;
  probabilityPpm: number;
  isDelivery: boolean;
  position: number;
}

export interface MrtDecisionPoint {
  id: string;
  code: string;
  name: string;
  triggerKind: "scheduled" | "wearable";
  scheduleTimes: string[] | null;
  scheduleDays: number[] | null;
  wearableCondition: Record<string, unknown> | null;
  conditionSemantics: string | null;
  cooldownMinutes: number;
  cooldownBasis: "delivered" | "available";
  dailyCap: number;
  occurrenceRowCap: number;
  maxStalenessMinutes: number;
  graceMinutes: number;
  missedWindowPolicy: "skip" | "backfill";
  requiresArmAllocation: boolean;
  optionSetVersion: number;
  status: "draft" | "active" | "inactive";
  activatedAt: string | null;
  options: MrtTreatmentOption[];
  /** The running total the editor shows, computed server-side so the
   *  arithmetic is not duplicated (spec 21.4). */
  probabilitySum: number;
  probabilitySumTarget: number;
  editable: boolean;
}

export type SaveDecisionPointBody = StudyArg &
  Omit<
    MrtDecisionPoint,
    | "id"
    | "optionSetVersion"
    | "status"
    | "activatedAt"
    | "probabilitySum"
    | "probabilitySumTarget"
    | "editable"
  >;

export interface FindOccurrencesArgs extends StudyArg {
  page?: number;
  pageSize?: number;
  participantId?: string;
  decisionPointId?: string;
  dateFrom?: string;
  dateTo?: string;
  availability?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface MrtOccurrence {
  id: string;
  participantId: string;
  subjectId: string | null;
  decisionPointId: string;
  decisionPointCode: string;
  occurrenceIndex: number;
  triggeredAt: string;
  evaluatedAt: string;
  localDate: string;
  localTime: string;
  timeZone: string;
  utcOffsetMinutes: number;
  scheduleSlotKey: string | null;
  triggerKind: string;
  availability: string;
  availabilityDetail: Record<string, unknown> | null;
  isRandomized: boolean;
  /** How many further suppressed triggers on the same local day this row
   *  absorbed. Rendered as "×N" (spec 13.5). */
  suppressionCount: number;
  suppressed: boolean;
  delivered: boolean | null;
  deliveryRef: string | null;
  optionSetVersion: number;
  outcomeType: string | null;
  outcomeValue: string | null;
  outcomeObservedAt: string | null;
  /** Arm-equivalent for a blinded reader, so absent without `arm_view`. */
  optionCode?: string;
  randomizationProbabilityPpm?: number | null;
}

export interface OccurrencePage {
  items: MrtOccurrence[];
  total: number;
  page: number;
  pageSize: number;
  optionColumnVisible: boolean;
  availabilityValues: string[];
}

export interface OccurrenceExportArgs extends StudyArg {
  decisionPointId?: string;
  participantId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface SweepSummary {
  decisionPointId: string;
  studyId: string;
  status: "completed" | "skipped_locked" | "failed";
  windowStart: string | null;
  windowEnd: string | null;
  candidatesScanned: number;
  occurrencesCreated: number;
  occurrencesSuppressed: number;
  duplicatesSkipped: number;
  error?: string;
}

/* ---------------------------------------------------------- test bench ---- */

export interface TestBenchRunListItem {
  id: string;
  label: string;
  paramDigest: string;
  slotCount: number;
  createdAt?: string;
  createdBy?: string | null;
}

export interface TestBenchRunDetail {
  id: string;
  label: string;
  configSnapshot: Record<string, unknown>;
  paramDigest: string;
  recordedSlotCount: number;
  createdAt: string;
  currentSlotCount: number;
  currentSequenceChecksum: string;
  allocations: {
    total: number;
    distinctNumbers: number;
    distinctSlots: number;
    reversed: number;
    numbersUnique: boolean;
  };
  isolation: {
    location: string;
    reads: string;
    unit: number;
    applied: boolean;
  }[];
}

export interface SimulationOutcome {
  requested: number;
  allocated: number;
  failed: number;
  errorCounts: Record<string, number>;
  distinctNumbers: number;
  distinctSlots: number;
  numbersUnique: boolean;
  slotsUnique: boolean;
  concurrency: number;
}

/** One row of the demo console roster. Arm and block fields are ABSENT, not
 *  null, when the blind withholds them (spec 11.2). */
export interface RosterRow {
  participantId: string;
  subjectId: string;
  siteId: string | null;
  siteName: string | null;
  allocationId: string | null;
  randomizationNumber: number | null;
  armCode?: string;
  stratumKey: string | null;
  /** `stratumKey` with the site id resolved to its name and the gender/age
   *  tokens to their labels. Absent without `randomization.stratum_view`. */
  stratumLabel?: string | null;
  state: string | null;
  attemptSeq: number | null;
  allocatedAt: string | null;
  reversedAt: string | null;
  unblindedAt: string | null;
  blockId?: number | null;
  blockLength?: number | null;
  isRandomized: boolean;
}

/** One row per stratum that has slots. Arm-agnostic, so it is present at every
 *  blinding level; only the LABEL is gated. */
export interface StratumSlotCounts {
  stratumKey: string;
  stratumLabel?: string;
  available: number;
  consumed: number;
  retired: number;
  /** Current per-stratum list length - what `list/extend` must exceed. */
  generated: number;
}

export interface RosterSummary {
  participants: number;
  randomized: number;
  reversed: number;
  /** STUDY-WIDE. On a stratified study this can be non-zero while the stratum
   *  a given participant needs is empty, which is why `perStratum` exists. */
  slotsAvailable: number;
  slotsConsumed: number;
  slotsRetired: number;
  perStratum?: StratumSlotCounts[];
  /** Absent when the blind withholds arms - it is an unblinding channel. */
  perArm?: Record<string, number>;
}

export interface RosterResponse {
  rows: RosterRow[];
  summary: RosterSummary;
  blindingLevel: string;
  method: string | null;
  configStatus: string | null;
  audience: "self" | "blinded";
  armVisible: boolean;
}

/** `POST /randomization/testbench/participants`. */
export interface GeneratedParticipants {
  created: number;
  siteId: string;
  subjectIdFrom: string;
  subjectIdTo: string;
  genderCounts: Record<string, number>;
  yearOfBirthRange: { min: number; max: number };
  distinctYears: number;
}

/** `GET /randomization/testbench/flag`. */
export interface TestBenchFlag {
  isTestBench: boolean;
  studyStatus: string;
  hasAllocations: boolean;
  hasRuns: boolean;
  canEnable: boolean;
  canDisable: boolean;
  /** An i18n key from the randomization namespace, or null when unblocked. */
  blockedReason: string | null;
}

export interface RunComparison {
  identical: boolean;
  comparable: boolean;
  reason?: string;
  slotCountA: number;
  slotCountB: number;
  differences: {
    randomizationNumber: number;
    stratumKey: string;
    field: string;
    a: string | number | null;
    b: string | number | null;
  }[];
  runA: { id: string; label: string; paramDigest: string; slotCount: number };
  runB: { id: string; label: string; paramDigest: string; slotCount: number };
  paramDigestsMatch: boolean;
  slotCountsMatch: boolean;
  comparedAgainst: string;
}

export const randomizationApi = createApi({
  reducerPath: "randomizationApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "RandomizationConfig",
    "Slots",
    "Allocation",
    "Occurrences",
    "TestBenchRun",
    "Roster",
  ],
  endpoints: (builder) => ({
    /* ----------------------------------------------------- configuration --- */

    getConfig: builder.query<RandomizationConfig | null, { studyId: string }>({
      query: ({ studyId }) => ({ url: "/randomization", params: { studyId } }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    createConfig: builder.mutation<{ id: string }, SaveConfigBody>({
      query: (body) => ({ url: "/randomization", method: "POST", data: body }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    updateConfig: builder.mutation<{ id: string }, SaveConfigBody>({
      query: (body) => ({ url: "/randomization", method: "PATCH", data: body }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    deleteConfig: builder.mutation<void, { studyId: string }>({
      query: ({ studyId }) => ({
        url: "/randomization",
        method: "DELETE",
        params: { studyId },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    /** Writes nothing, so it invalidates nothing. */
    previewSequence: builder.mutation<SequencePreview, { studyId: string }>({
      query: (body) => ({
        url: "/randomization/preview",
        method: "POST",
        data: body,
      }),
    }),

    /** Sends only the study id — the draft is the source of truth (spec 20.6). */
    commitSequence: builder.mutation<CommitResult, { studyId: string }>({
      query: (body) => ({
        url: "/randomization/commit",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    patchBlinding: builder.mutation<
      { blindingLevel: BlindingLevel },
      { studyId: string; blindingLevel: BlindingLevel; reason: string }
    >({
      query: (body) => ({
        url: "/randomization/blinding",
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        // The blinding level decides whether arms are shaped out of every
        // allocation and slot payload, so both caches are stale now.
        { type: "Slots", id: studyId },
        { type: "Roster", id: studyId },
        "Allocation",
      ],
    }),

    verifySequence: builder.query<
      {
        pass: boolean;
        storedChecksum: string | null;
        recomputedChecksum: string;
        slotCount: number;
      },
      { studyId: string }
    >({
      query: ({ studyId }) => ({
        url: "/randomization/verify",
        params: { studyId },
      }),
    }),

    /* -------------------------------------------- factor discovery -------- */

    /**
     * Every question a factor may bind to.
     *
     * SEPARATE FROM `getConfig` ON PURPOSE. The picker opens on a fraction of
     * visits to the configuration screen, and a study with sixty eligible
     * questions has no business loading them alongside the configuration on a
     * screen that may never open it.
     *
     * `draftConfigId` marks the questions the draft already binds, so the
     * picker can show them as taken rather than letting a designer add the same
     * question twice and discover it at commit.
     */
    getFactorCandidates: builder.query<
      FactorCandidate[],
      { studyId: string; draftConfigId?: string | null }
    >({
      query: ({ studyId, draftConfigId }) => ({
        url: "/randomization/factor-candidates",
        params: draftConfigId ? { studyId, draftConfigId } : { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    /**
     * The study's sites, for the levels of a `site` factor.
     *
     * A site factor's levels are sent by the client — one per site, each
     * carrying a `studySiteId` — because which sites a design stratifies on is
     * a design decision. The picker therefore needs the list, and it is served
     * from the randomization module so that the configuration screen has one
     * API surface rather than two.
     */
    getSiteCandidates: builder.query<SiteCandidate[], { studyId: string }>({
      query: ({ studyId }) => ({
        url: "/randomization/site-candidates",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    /* ------------------------------------------------ the lifecycle ------- */

    /**
     * Put the committed version into force. SF-D23.
     *
     * NO VERSION ID IN THE BODY, deliberately: a study has at most one
     * committed version waiting, and naming it would let a stale screen
     * activate a version that has since been withdrawn and re-committed.
     *
     * It is activation, not commit, that retires the superseded version's
     * remaining slots — so this invalidates the slot caches as well.
     */
    activateConfig: builder.mutation<
      ActivateResult,
      { studyId: string; idempotencyKey: string }
    >({
      query: ({ idempotencyKey, ...data }) => ({
        url: "/randomization/activate",
        method: "POST",
        data,
        // Activation retires the superseded version's remaining slots. A
        // double-submit without a key would run that retirement twice against
        // two different versions.
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
        { type: "Roster", id: studyId },
        "Allocation",
      ],
    }),

    /** Discard a committed sequence and return the version to draft. SF-D24.
     *  Refused once one allocation exists — the server hides nothing, but the
     *  screen should hide the control rather than disable it. */
    withdrawConfig: builder.mutation<
      WithdrawResult,
      { studyId: string; reason: string; idempotencyKey: string }
    >({
      query: ({ idempotencyKey, ...data }) => ({
        url: "/randomization/withdraw",
        method: "POST",
        data,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    /**
     * Amend the active version, producing a new COMMITTED version. SFT-13.
     *
     * The new version is not in force until it is activated, which is a
     * separate call. The response carries the diff, computed while both
     * versions are in hand, so the confirmation screen can show exactly what
     * is changing before anyone activates it.
     */
    amendConfig: builder.mutation<
      AmendResult,
      {
        studyId: string;
        reason: string;
        arms: { code: string; name?: string; weight: number; position: number }[];
        factors?: SaveFactorBody[];
        streamPolicy?: StreamPolicy;
        changes?: Record<string, unknown>;
        idempotencyKey: string;
      }
    >({
      query: ({ idempotencyKey, ...data }) => ({
        url: "/randomization/amend",
        method: "POST",
        data,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    /**
     * Add a sequence for a site that opened after commit. SF-D8.
     *
     * The only sanctioned change to a LIVE configuration, and it is purely
     * additive: every existing stratum keeps its key, every existing slot keeps
     * its stratum, and only the combinations that could not previously occur
     * get sequences of their own.
     */
    addSiteStratum: builder.mutation<
      AddSiteStratumResult,
      {
        studyId: string;
        studySiteId: string;
        label?: string | null;
        idempotencyKey: string;
      }
    >({
      query: ({ idempotencyKey, ...data }) => ({
        url: "/randomization/site-stratum",
        method: "POST",
        data,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    /* ------------------------------------------------------- participant --- */

    getAllocation: builder.query<
      ParticipantAllocation,
      { studyId: string; participantId: string }
    >({
      query: ({ studyId, participantId }) => ({
        url: `/randomization/participants/${participantId}`,
        params: { studyId },
      }),
      providesTags: (_r, _e, { participantId }) => [
        { type: "Allocation", id: participantId },
      ],
    }),

    /**
     * The participant's factors, one row each. SFT-25.
     *
     * TAGGED `Allocation` BY PARTICIPANT, so randomizing invalidates it. The
     * rows change shape at that moment — before allocation they are the live
     * outstanding view, after it they are the snapshot — and a stale cache
     * would leave the dialog showing what the participant needed to answer
     * after they have already been randomized on it.
     */
    getParticipantFactors: builder.query<
      ParticipantFactorStatus,
      { studyId: string; participantId: string }
    >({
      query: ({ studyId, participantId }) => ({
        url: `/randomization/participants/${participantId}/factors`,
        params: { studyId },
      }),
      providesTags: (_r, _e, { participantId }) => [
        { type: "Allocation", id: participantId },
      ],
    }),

    /**
     * Every participant who could be randomized right now. SFT-17.
     *
     * IDS ONLY — the list screen already holds the participants. One
     * set-based query for the study, because a per-participant readiness call
     * on a page of fifty is a hundred round trips.
     */
    getReadyParticipants: builder.query<
      { participantIds: string[]; count: number },
      { studyId: string }
    >({
      query: ({ studyId }) => ({
        url: "/randomization/ready-participants",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        "Allocation",
      ],
    }),

    getAllocationHistory: builder.query<
      ParticipantAllocation[],
      { studyId: string; participantId: string }
    >({
      query: ({ studyId, participantId }) => ({
        url: `/randomization/participants/${participantId}/history`,
        params: { studyId },
      }),
      providesTags: (_r, _e, { participantId }) => [
        { type: "Allocation", id: participantId },
      ],
    }),

    allocateParticipant: builder.mutation<
      ParticipantAllocation,
      {
        studyId: string;
        participantId: string;
        triggerSource: string;
        idempotencyKey: string;
      }
    >({
      query: ({ studyId, participantId, triggerSource, idempotencyKey }) => ({
        url: `/randomization/participants/${participantId}/allocate`,
        method: "POST",
        data: { studyId, triggerSource },
        // Spec 20.6, 21.8: a repeat within 24 hours returns the original
        // result rather than randomizing the participant twice.
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { participantId, studyId }) => [
        { type: "Allocation", id: participantId },
        { type: "Slots", id: studyId },
        { type: "Roster", id: studyId },
      ],
    }),

    reverseAllocation: builder.mutation<
      { allocationId: string; retiredRandomizationNumber: number | null },
      {
        studyId: string;
        participantId: string;
        reasonCode: string;
        reason: string;
        idempotencyKey: string;
      }
    >({
      query: ({ participantId, idempotencyKey, ...data }) => ({
        url: `/randomization/participants/${participantId}/reverse`,
        method: "POST",
        data,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { participantId, studyId }) => [
        { type: "Allocation", id: participantId },
        { type: "Slots", id: studyId },
        { type: "Roster", id: studyId },
      ],
    }),

    /**
     * Returns the arm ONCE (spec 11.3).
     *
     * The result is deliberately NOT cached into the Allocation tag: the arm
     * is in this response and nowhere else, and writing it into the cache
     * would leave it on screen after a refresh, which is precisely the
     * persistent visibility the specification forbids.
     */
    unblindParticipant: builder.mutation<
      { armCode: string; eventId: string },
      {
        studyId: string;
        participantId: string;
        reasonCode: string;
        reason: string;
        safetyReference?: string;
        idempotencyKey: string;
      }
    >({
      query: ({ participantId, idempotencyKey, ...data }) => ({
        url: `/randomization/participants/${participantId}/unblind`,
        method: "POST",
        data,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
      invalidatesTags: (_r, _e, { participantId }) => [
        { type: "Allocation", id: participantId },
      ],
    }),

    /* -------------------------------------------------------------- list --- */

    getSlots: builder.query<SlotPage, FindSlotsArgs>({
      query: ({ studyId, ...params }) => ({
        url: "/randomization/slots",
        params: { studyId, ...params },
      }),
      providesTags: (_r, _e, { studyId }) => [{ type: "Slots", id: studyId }],
    }),

    getRetiredSlotsReport: builder.query<RetiredSlotsReport, StudyArg>({
      query: ({ studyId }) => ({
        url: "/randomization/reports/retired-slots",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [{ type: "Slots", id: studyId }],
    }),

    getStratumDriftReport: builder.query<StratumDriftReport, StudyArg>({
      query: ({ studyId }) => ({
        url: "/randomization/reports/stratum-drift",
        params: { studyId },
      }),
      providesTags: ["Allocation"],
    }),

    getUnblindingEventsReport: builder.query<UnblindingEventsReport, StudyArg>({
      query: ({ studyId }) => ({
        url: "/randomization/reports/unblinding-events",
        params: { studyId },
      }),
      providesTags: ["Allocation"],
    }),

    getGenderQuestion: builder.query<GenderQuestion, StudyArg>({
      query: ({ studyId }) => ({
        url: "/randomization/stratification/gender-question",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    getYearOfBirthQuestion: builder.query<YearOfBirthQuestion, StudyArg>({
      query: ({ studyId }) => ({
        url: "/randomization/stratification/year-of-birth-question",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
      ],
    }),

    generateStrata: builder.mutation<
      { generated: number; strata: string[]; sequenceChecksum: string | null },
      { studyId: string; reason: string }
    >({
      query: (data) => ({
        url: "/randomization/strata/generate",
        method: "POST",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    extendList: builder.mutation<
      { added: number; sequenceChecksum: string | null },
      {
        studyId: string;
        stratumKey: string;
        newListLength: number;
        reason: string;
      }
    >({
      query: (data) => ({
        url: "/randomization/list/extend",
        method: "POST",
        data,
      }),
      // Roster too: the demo console reads `summary.slotsAvailable` from the
      // roster, and extending the list is precisely the action that changes
      // it. Without this the console still shows the exhausted banner after a
      // successful extension.
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
        { type: "Roster", id: studyId },
      ],
    }),

    /**
     * Multipart. `data` is a FormData, and the axios adapter must not set a
     * JSON content type for it — the browser adds the multipart boundary.
     */
    uploadList: builder.mutation<
      { rowCount: number; fileSha256: string; sequenceChecksum: string },
      { studyId: string; file: File }
    >({
      query: ({ studyId, file }) => {
        const form = new FormData();
        form.append("file", file);
        form.append("studyId", studyId);
        return {
          url: "/randomization/list/upload",
          method: "POST",
          data: form,
        };
      },
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    /**
     * Requests the file. Writes nothing the caches hold, so it invalidates
     * nothing — the export appears in the export manager, not here.
     */
    exportList: builder.mutation<ExportRecord, { studyId: string; stratumKey?: string }>({
      query: (data) => ({
        url: "/randomization/list/export",
        method: "POST",
        data,
      }),
    }),

    exportAllocations: builder.mutation<ExportRecord, StudyArg>({
      query: (data) => ({
        url: "/randomization/allocations/export",
        method: "POST",
        data,
      }),
    }),

    /* --------------------------------------------------------------- MRT --- */

    getDecisionPoints: builder.query<
      { items: MrtDecisionPoint[]; total: number },
      StudyArg
    >({
      query: ({ studyId }) => ({
        url: "/randomization/mrt/decision-points",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: `dp-${studyId}` },
      ],
    }),

    createDecisionPoint: builder.mutation<
      { id: string; warnings: MrtValidationIssue[] },
      SaveDecisionPointBody
    >({
      query: (data) => ({
        url: "/randomization/mrt/decision-points",
        method: "POST",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: `dp-${studyId}` },
      ],
    }),

    updateDecisionPoint: builder.mutation<
      { id: string; optionSetVersion: number; warnings: MrtValidationIssue[] },
      SaveDecisionPointBody & { id: string; reason: string }
    >({
      query: ({ id, ...data }) => ({
        url: `/randomization/mrt/decision-points/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: `dp-${studyId}` },
      ],
    }),

    activateDecisionPoint: builder.mutation<
      { id: string; status: string; warnings: MrtValidationIssue[] },
      { studyId: string; id: string }
    >({
      query: ({ studyId, id }) => ({
        url: `/randomization/mrt/decision-points/${id}/activate`,
        method: "POST",
        data: { studyId },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: `dp-${studyId}` },
      ],
    }),

    deactivateDecisionPoint: builder.mutation<
      { id: string; status: string },
      { studyId: string; id: string }
    >({
      query: ({ studyId, id }) => ({
        url: `/randomization/mrt/decision-points/${id}/deactivate`,
        method: "POST",
        data: { studyId },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: `dp-${studyId}` },
      ],
    }),

    getOccurrences: builder.query<OccurrencePage, FindOccurrencesArgs>({
      query: ({ studyId, ...params }) => ({
        url: "/randomization/mrt/occurrences",
        params: { studyId, ...params },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: studyId },
      ],
    }),

    exportOccurrences: builder.mutation<ExportRecord, OccurrenceExportArgs>({
      query: (data) => ({
        url: "/randomization/mrt/occurrences/export",
        method: "POST",
        data,
      }),
    }),

    exportOccurrenceVerification: builder.mutation<
      ExportRecord,
      OccurrenceExportArgs
    >({
      query: (data) => ({
        url: "/randomization/mrt/occurrences/verify-export",
        method: "POST",
        data,
      }),
    }),

    /**
     * Runs the scheduler for one decision point, synchronously.
     *
     * Invalidates the occurrence cache because it creates occurrences — that
     * is the whole point of the endpoint.
     */
    runDecisionPointNow: builder.mutation<
      SweepSummary,
      { studyId: string; id: string }
    >({
      query: ({ studyId, id }) => ({
        url: `/randomization/mrt/decision-points/${id}/run-now`,
        method: "POST",
        data: { studyId },
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: studyId },
      ],
    }),

    simulateWearable: builder.mutation<
      { outcomes: unknown[]; failed: boolean },
      {
        studyId: string;
        participantId: string;
        readings: { metric: string; value: number; observedAt: string }[];
        lastDeviceDataAt?: string;
      }
    >({
      query: (data) => ({
        url: "/randomization/mrt/simulate-wearable",
        method: "POST",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "Occurrences", id: studyId },
      ],
    }),

    /* -------------------------------------------------------- test bench --- */

    getRoster: builder.query<
      RosterResponse,
      StudyArg & { asAudience?: "self" | "blinded" }
    >({
      query: ({ studyId, asAudience }) => ({
        url: "/randomization/testbench/roster",
        params: { studyId, ...(asAudience ? { asAudience } : {}) },
      }),
      providesTags: (_r, _e, { studyId }) => [{ type: "Roster", id: studyId }],
    }),

    generateTestParticipants: builder.mutation<
      GeneratedParticipants,
      StudyArg & { count: number; siteId?: string; includeAllGenders?: boolean }
    >({
      query: (body) => ({
        url: "/randomization/testbench/participants",
        method: "POST",
        data: body,
      }),
      // New participants change who can be allocated, so the run list and the
      // allocation views are both stale.
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "TestBenchRun", id: studyId },
        { type: "Roster", id: studyId },
        "Allocation",
      ],
    }),

    /* -------------------------------------------- test bench flag --- */

    getTestBenchFlag: builder.query<TestBenchFlag, StudyArg>({
      query: ({ studyId }) => ({
        url: "/randomization/testbench/flag",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "TestBenchRun", id: studyId },
      ],
    }),

    setTestBenchFlag: builder.mutation<
      { isTestBench: boolean; changed: boolean },
      StudyArg & { isTestBench: boolean }
    >({
      query: (body) => ({
        url: "/randomization/testbench/flag",
        method: "PATCH",
        data: body,
      }),
      // The flag changes what every other randomization screen may do, so the
      // whole study's randomization cache is dropped rather than one tag.
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "TestBenchRun", id: studyId },
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
      ],
    }),

    getTestBenchRuns: builder.query<
      { items: TestBenchRunListItem[]; total: number },
      StudyArg
    >({
      query: ({ studyId }) => ({
        url: "/randomization/testbench/runs",
        params: { studyId },
      }),
      providesTags: (_r, _e, { studyId }) => [
        { type: "TestBenchRun", id: studyId },
      ],
    }),

    getTestBenchRun: builder.query<
      TestBenchRunDetail,
      { studyId: string; id: string }
    >({
      query: ({ studyId, id }) => ({
        url: `/randomization/testbench/runs/${id}`,
        params: { studyId },
      }),
      providesTags: (_r, _e, { id }) => [{ type: "TestBenchRun", id }],
    }),

    createTestBenchRun: builder.mutation<
      TestBenchRunListItem & { sequenceChecksum: string },
      { studyId: string; label: string }
    >({
      query: (data) => ({
        url: "/randomization/testbench/runs",
        method: "POST",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "TestBenchRun", id: studyId },
      ],
    }),

    simulateAllocations: builder.mutation<
      SimulationOutcome,
      {
        studyId: string;
        id: string;
        label: string;
        participantCount: number;
        concurrency: number;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/randomization/testbench/runs/${id}/allocate`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId, id }) => [
        { type: "TestBenchRun", id },
        { type: "TestBenchRun", id: studyId },
        { type: "Slots", id: studyId },
        "Allocation",
      ],
    }),

    compareTestBenchRuns: builder.mutation<
      RunComparison,
      { studyId: string; id: string; otherId: string }
    >({
      query: ({ studyId, id, otherId }) => ({
        url: `/randomization/testbench/runs/${id}/compare/${otherId}`,
        method: "POST",
        data: { studyId },
      }),
    }),

    exportTestBenchRun: builder.mutation<
      ExportRecord,
      { studyId: string; id: string }
    >({
      query: ({ studyId, id }) => ({
        url: `/randomization/testbench/runs/${id}/export`,
        method: "POST",
        data: { studyId },
      }),
    }),

    /**
     * Reset. Invalidates EVERYTHING for the study: it deletes the slots and
     * the allocations and returns the config to draft, so every cached
     * randomization payload is stale.
     */
    resetTestBench: builder.mutation<
      {
        slotsDeleted: number;
        allocationsDeleted: number;
        streamSecretPreserved: boolean;
      },
      StudyArg
    >({
      query: (data) => ({
        url: "/randomization/testbench/reset",
        method: "POST",
        data,
      }),
      invalidatesTags: (_r, _e, { studyId }) => [
        { type: "RandomizationConfig", id: studyId },
        { type: "Slots", id: studyId },
        { type: "TestBenchRun", id: studyId },
        "Allocation",
        "Occurrences",
      ],
    }),
  }),
});

export const {
  useGetConfigQuery,
  useCreateConfigMutation,
  useUpdateConfigMutation,
  useDeleteConfigMutation,
  usePreviewSequenceMutation,
  useCommitSequenceMutation,
  usePatchBlindingMutation,
  useVerifySequenceQuery,
  // Stratification factors (SFT-16, SFT-22) and the configuration lifecycle
  // (SFT-12, SFT-13, SFT-20, SFT-24).
  useGetFactorCandidatesQuery,
  useLazyGetFactorCandidatesQuery,
  useGetSiteCandidatesQuery,
  useActivateConfigMutation,
  useWithdrawConfigMutation,
  useAmendConfigMutation,
  useAddSiteStratumMutation,
  useGetAllocationQuery,
  useGetAllocationHistoryQuery,
  useGetParticipantFactorsQuery,
  useGetReadyParticipantsQuery,
  useAllocateParticipantMutation,
  useReverseAllocationMutation,
  useUnblindParticipantMutation,
  useGetSlotsQuery,
  useGetRetiredSlotsReportQuery,
  useGetStratumDriftReportQuery,
  useGetUnblindingEventsReportQuery,
  useGetGenderQuestionQuery,
  useGetYearOfBirthQuestionQuery,
  useGenerateStrataMutation,
  useExtendListMutation,
  useUploadListMutation,
  useExportListMutation,
  useExportAllocationsMutation,
  useGetDecisionPointsQuery,
  useCreateDecisionPointMutation,
  useUpdateDecisionPointMutation,
  useActivateDecisionPointMutation,
  useDeactivateDecisionPointMutation,
  useGetOccurrencesQuery,
  useExportOccurrencesMutation,
  useExportOccurrenceVerificationMutation,
  useRunDecisionPointNowMutation,
  useSimulateWearableMutation,
  useGenerateTestParticipantsMutation,
  useGetRosterQuery,
  useGetTestBenchFlagQuery,
  useSetTestBenchFlagMutation,
  useGetTestBenchRunsQuery,
  useGetTestBenchRunQuery,
  useCreateTestBenchRunMutation,
  useSimulateAllocationsMutation,
  useCompareTestBenchRunsMutation,
  useExportTestBenchRunMutation,
  useResetTestBenchMutation,
} = randomizationApi;
